import React, { useState, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ShimmerButton } from "@/components/ui/shimmer-button";
import { SparklesCoreLazyWrapper } from "@/components/ui/SparklesCoreLazy";
import { PlaceholdersAndVanishInput } from "@/components/ui/placeholders-and-vanish-input";
import { SearchAutocomplete } from "@/components/SearchAutocomplete";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { ArrowRight, Play, Search, Code2, Database, Palette, Terminal, Server, BarChart3 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCategories } from "@/hooks/useCategories";
import { useSkills } from "@/hooks/useSkills";
import techProfessional1 from "@/assets/tech-professional-1.webp";
import techProfessional2 from "@/assets/tech-professional-2.webp";
import techProfessional3 from "@/assets/tech-professional-3.webp";
import heroBackground from "@/assets/hero-background.webp";

const HeroAlt = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const { data: skills = [] } = useSkills();

  // Memoize skill name map to avoid recreating on every render
  const skillNameMap = useMemo(() => ({
    'React': 'React',
    'Python': 'Python',
    'Design': 'UI/UX Design',
    'Node.js': 'Node.js',
    'DevOps': 'DevOps',
    'Data': 'Data Analysis'
  }), []);

  // Memoize function to avoid recreating on every render
  const getSkillIdByTechName = useCallback((techName: string): string | undefined => {
    const skillName = skillNameMap[techName];
    if (!skillName) return undefined;
    
    const skill = skills.find(s => s.name.toLowerCase() === skillName.toLowerCase());
    return skill?.id;
  }, [skills, skillNameMap]);

  // Memoize arrays to avoid recreating on every render
  const placeholders = useMemo(() => [
    "React Developer",
    "Senior React Developer",
    "React + TypeScript Developer",
    "Full Stack React Developer",
    "React Native Developer",
  ], []);

  const techNames = useMemo(() => [
    "React", "Python", "AI", "Node.js", "TypeScript", "AWS", 
    "Docker", "Kubernetes", "GraphQL", "MongoDB", "PostgreSQL",
    "Vue", "Angular", "Django", "FastAPI", "TensorFlow"
  ], []);

  // Memoize event handlers to avoid recreating on every render
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleSearchSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Removed console.log for production
  }, []);

  const handleProjectSubmit = useCallback(() => {
    navigate('/company-registration', { state: { projectDescription } });
  }, [navigate, projectDescription]);

  // Memoize tech button handlers to avoid recreating on every render
  const handleTechClick = useCallback((techName: string, fallbackName?: string) => {
    const skillId = getSkillIdByTechName(techName);
    if (skillId) {
      navigate(`/stack-selection?skill=${skillId}`);
    } else {
      navigate(`/stack-selection?skillName=${fallbackName || techName}`);
    }
  }, [navigate, getSkillIdByTechName]);

  return (
    <div className="min-h-[70vh] w-full relative flex flex-col items-center justify-center overflow-hidden py-16">
      {/* Hero Background Image - Rendered first for LCP optimization */}
      <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
        <img 
          src={heroBackground} 
          alt="" 
          className="w-full h-full object-cover"
          width={1920}
          height={1080}
          decoding="sync"
          loading="eager"
          fetchPriority="high"
          sizes="100vw"
          style={{ display: 'block', willChange: 'auto' }}
        />
      </div>

      {/* Sparkles Background - on top of image */}
      <div className="w-full h-full absolute inset-0 z-10">
        <SparklesCoreLazyWrapper
          id="tsparticlesfullpage"
          background="transparent"
          minSize={0.4}
          maxSize={1}
          particleDensity={60}
          className="w-full h-full"
          particleColor="#41A0DC"
        />
      </div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0.0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{
          delay: 0.3,
          duration: 0.8,
          ease: "easeInOut",
        }}
        className="relative z-30 w-full px-6"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Content */}
            <div className="space-y-8">
              <div className="space-y-0">
                <motion.h1 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.6 }}
                  className="text-[2.75625rem] lg:text-[3.4453125rem] font-bold leading-tight text-white"
                >
                  Hire Faster.
                </motion.h1>
                <motion.h2 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.6 }}
                  className="text-[2.75625rem] lg:text-[3.4453125rem] font-bold leading-tight text-white"
                >
                  Hire Smarter.
                </motion.h2>
              </div>
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.6 }}
                className="text-medium text-white leading-relaxed"
              >
                Hire top LATAM software engineers for U.S. projects. AI-powered vetting ensures the perfect fit, transparent results, and faster onboarding — no surprises, just productivity.
              </motion.p>
              
              {/* CTA Buttons */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 pt-4"
              >
                <ShimmerButton 
                  size="xl" 
                  className="shadow-hero bg-[#41A0DC] hover:bg-[#41A0DC]/90 text-[#0F2341] font-semibold transition-colors"
                  onClick={() => navigate('/stack-selection')}
                >
                  Hire Your Team
                </ShimmerButton>
                <ShimmerButton 
                  size="xl" 
                  className="shadow-hero bg-gray-700 hover:bg-gray-600 text-white transition-colors"
                  onClick={() => navigate('/contact/book-call')}
                >
                  Book a Call
                </ShimmerButton>
              </motion.div>
            </div>

            {/* Right Column - Quick Search with Professional Images */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.9, duration: 0.6 }}
              className="hidden lg:flex flex-col items-center space-y-8"
            >
              {/* Quick Search */}
              <div className="relative w-full max-w-md z-[9999]">
                <div className="relative">
                  <PlaceholdersAndVanishInput
                    placeholders={placeholders}
                    onChange={handleSearchChange}
                    onSubmit={handleSearchSubmit}
                    label="Search for job positions and roles"
                    id="hero-alt-search-input"
                  />
                  <Search className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400 pointer-events-none" />
                </div>
                <SearchAutocomplete 
                  searchQuery={searchQuery}
                  onSelect={() => setSearchQuery("")}
                />
              </div>

              {/* Technology Icons - Responsive layout below search */}
              <div className="flex justify-center items-center gap-2 sm:gap-3 md:gap-4 flex-wrap max-w-4xl mt-6">
                {/* React */}
                <button
                  onClick={() => handleTechClick('React')}
                  className="flex flex-col items-center space-y-1 sm:space-y-2 cursor-pointer"
                >
                  <div className="relative w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-blue-50 to-sky-100 flex items-center justify-center border-2 border-sky-200 shadow-md hover:scale-110 transition-all duration-300">
                    <Code2 className="w-5 h-5 sm:w-5 sm:h-5 md:w-6 md:h-6" style={{ color: '#41A0DC' }} />
                  </div>
                  <span className="text-[10px] sm:text-xs font-semibold text-white bg-black/20 px-1.5 py-0.5 rounded-full shadow-md backdrop-blur-sm">React</span>
                </button>

                {/* Python */}
                <button
                  onClick={() => handleTechClick('Python')}
                  className="flex flex-col items-center space-y-1 sm:space-y-2 cursor-pointer"
                >
                  <div className="relative w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-blue-50 to-sky-100 flex items-center justify-center border-2 border-sky-200 shadow-md hover:scale-110 transition-all duration-300">
                    <Terminal className="w-5 h-5 sm:w-5 sm:h-5 md:w-6 md:h-6" style={{ color: '#41A0DC' }} />
                  </div>
                  <span className="text-[10px] sm:text-xs font-semibold text-white bg-black/20 px-1.5 py-0.5 rounded-full shadow-md backdrop-blur-sm">Python</span>
                </button>

                {/* Design */}
                <button
                  onClick={() => handleTechClick('Design', 'UI/UX')}
                  className="flex flex-col items-center space-y-1 sm:space-y-2 cursor-pointer"
                >
                  <div className="relative w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-blue-50 to-sky-100 flex items-center justify-center border-2 border-sky-200 shadow-md hover:scale-110 transition-all duration-300">
                    <Palette className="w-5 h-5 sm:w-5 sm:h-5 md:w-6 md:h-6" style={{ color: '#41A0DC' }} />
                  </div>
                  <span className="text-[10px] sm:text-xs font-semibold text-white bg-black/20 px-1.5 py-0.5 rounded-full shadow-md backdrop-blur-sm">Design</span>
                </button>

                {/* Node.js */}
                <button
                  onClick={() => handleTechClick('Node.js')}
                  className="flex flex-col items-center space-y-1 sm:space-y-2 cursor-pointer"
                >
                  <div className="relative w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-blue-50 to-sky-100 flex items-center justify-center border-2 border-sky-200 shadow-md hover:scale-110 transition-all duration-300">
                    <Server className="w-5 h-5 sm:w-5 sm:h-5 md:w-6 md:h-6" style={{ color: '#41A0DC' }} />
                  </div>
                  <span className="text-[10px] sm:text-xs font-semibold text-white bg-black/20 px-1.5 py-0.5 rounded-full shadow-md backdrop-blur-sm">Node.js</span>
                </button>

                {/* DevOps */}
                <button
                  onClick={() => handleTechClick('DevOps')}
                  className="flex flex-col items-center space-y-1 sm:space-y-2 cursor-pointer"
                >
                  <div className="relative w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-blue-50 to-sky-100 flex items-center justify-center border-2 border-sky-200 shadow-md hover:scale-110 transition-all duration-300">
                    <Database className="w-5 h-5 sm:w-5 sm:h-5 md:w-6 md:h-6" style={{ color: '#41A0DC' }} />
                  </div>
                  <span className="text-[10px] sm:text-xs font-semibold text-white bg-black/20 px-1.5 py-0.5 rounded-full shadow-md backdrop-blur-sm">DevOps</span>
                </button>

                {/* Data */}
                <button
                  onClick={() => handleTechClick('Data')}
                  className="flex flex-col items-center space-y-1 sm:space-y-2 cursor-pointer"
                >
                  <div className="relative w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-blue-50 to-sky-100 flex items-center justify-center border-2 border-sky-200 shadow-md hover:scale-110 transition-all duration-300">
                    <BarChart3 className="w-5 h-5 sm:w-5 sm:h-5 md:w-6 md:h-6" style={{ color: '#41A0DC' }} />
                  </div>
                  <span className="text-[10px] sm:text-xs font-semibold text-white bg-black/20 px-1.5 py-0.5 rounded-full shadow-md backdrop-blur-sm">Data</span>
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default HeroAlt;
